import style from './style.scss';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import translations from './translations';
import WinnersTable from './WinnersTable';
import AwardsTable from './AwardsTable';
import PrizesWonTable from './PrizesWonTable';
import Button from './common/Button';
import {
    updateJoinState,
    useDynamicReducerSelector,
    useDynamicReducerDispatch,
    updatePrizes,
    updateLastWinners,
    updateStatus,
    updateRules,
    updateEndTime,
    updateEligible,
    updateStartTime,
    updateMysteryParcelId,
    updatePrizesWon,
    updateParticipatingGamesState,
    updateDisplayName,
    updateRegistrationStatus,
    updateStatusDelay,
    updateTermsAndConditionsUrl,
    updateBannerUrl,
    updateCustomAttributes
} from './state';
import {
    getLastWinners,
    getPrizes,
    useDetailedView,
    usePossessionInfo,
    join,
    getJoined,
    getStatus,
    leave,
    getRules,
    getEventEndTime,
    getEligible,
    getEventStartTime,
    getMysteryParcelId,
    getPrizesWon,
    isCompleted,
    getParticipatingGames,
    getDisplayName,
    getRegistrationStatus,
    getTermsAndConditionsUrl,
    getPossessionInfo,
    getBannerUrl,
    getCustomAttributes
} from './network';
import Rules from './components/Rules';
import Status from './components/Status';

import classNames from './common/classNames';
import { useDeviceSelector } from 'js/store/hooks';
import { pick } from 'ramda';
import { buildWinnersUpdater } from './buildWinnersUpdater';
import HeaderBanner from '../../common/HeaderBanner';
import { getLocationParam } from 'js/utils/url.util';
import GamesView from '../../common/GamesView';
import Media from '../Media';
import CountDownBadge from '../../../CasinoLeaderBoard/common/CountDown/CountDownBadge';
import { TermsAndConditions } from '../TermsAndConditions';
import { Skeleton } from '../Skeleton';
import { SkeletonWithError } from '../Skeleton/SkeletonWithError';
import useTranslate from './hooks/useTranslate';
import { publicEventsAPIService } from 'js/services/publicEventsAPI.service';
import { useCommonProperties } from '../analytics/useCommonProperties';
import { usePortletConfig } from 'js/components/system/Portlet';
import { DetailsInnerHeader } from '../../common/DetailsInnerHeader';

const Panel = ({ children }) => <div className={style.block}>
    <div className={style.panel}>
        {children}
    </div>
</div>;

export const extractMysteryParcelId = () => {
    const matchResult = window.location.pathname.match(/\/mystery-parcel\/(\d+|test)/);

    return matchResult ? matchResult[1] : undefined;
};

const handleMobileView = () => {
    const smallerEdge = Math.min(window.innerHeight, window.innerWidth);
    if (smallerEdge >= 400) {
        return style.dataWrapperLarge;
    } else if (smallerEdge > 360) {
        return style.dataWrapperMedium;
    }

    return style.dataWrapperSmall;
};

const sortPrizes = (prizes) => {
    prizes.sort((a, b) => {
        if (a.finalPrize) {
            return -1;
        } else if (b.finalPrize) {
            return 1;
        }

        return 0;
    });
    return prizes;
};

const isNotStartedRegistrationStatus = 'NOT_STARTED';
const isFinishedRegistrationStatus = 'FINISHED';
const isInternalRegistrationStatus = 'INTERNAL';

let possessionsIterations = [];
let timer;
let activePossessionIteration = 0;

let isAlreadyScrolledToBottom = false;
let isReceivedDetailsData = false;

const lastWinnersDefaultSize = 5;

export default () => {
    const [isShowPossessionInfo, setIsShowPossessionInfo] = useState(false);
    const translationsKeyPrefix = 'playtech.mobile.mystery-parcel-page.';
    const hideGamePanel = getLocationParam('hideGamePanel') === '1';
    const { lastWinnersConfig, lastWinnersCount } = usePortletConfig();

    const dispatch = useDynamicReducerDispatch();
    const { joinError, joining, joined, hasNickname, nicknameNeeded } = useDynamicReducerSelector(state => state.join);
    const participatingGames = useDynamicReducerSelector(state => state.participatingGames);
    const rules = useDynamicReducerSelector(state => state.rules);
    const status = useDynamicReducerSelector(state => state.status);
    const displayName = useDynamicReducerSelector(state => state.displayName);
    const registrationStatus = useDynamicReducerSelector(state => state.registrationStatus);
    const endTime = useDynamicReducerSelector(state => state.eventEndTime);
    const startTime = useDynamicReducerSelector(state => state.eventStartTime);
    const termsAndConditionsUrl = useDynamicReducerSelector(state => state.termsAndConditionsUrl);
    const bannerUrl = useDynamicReducerSelector(state => state.bannerUrl);
    const customAttributes = useDynamicReducerSelector(state => state.customAttributes);
    const mysteryParcelId = useDynamicReducerSelector(state => state.mysteryParcelId);

    const computeInitialTab = useCallback(() => {
        return getLocationParam('activeTab') === 'rules' || !joined ? 1 : 0;
    }, [joined]);

    const commonProperties = useCommonProperties();

    const trackScrollToBottom = useCallback(() => {
        if (window.innerHeight + window.scrollY >= document.body.offsetHeight) {
            if (!isAlreadyScrolledToBottom) {
                publicEventsAPIService.trigger('player-scrolls-down',
                    { ...commonProperties, ...{ 'overlay.engagementId': mysteryParcelId } });
            }
            isAlreadyScrolledToBottom = true;
        }
    }, [commonProperties, mysteryParcelId]);

    const [activePage, setActivePage] = useState(() => computeInitialTab());
    useEffect(() => setActivePage(computeInitialTab()), [joined, computeInitialTab]);
    useEffect(() => {
        window.addEventListener('scroll', trackScrollToBottom, false);
        return () => {
            window.removeEventListener('scroll', trackScrollToBottom, false);
        };
    }, [trackScrollToBottom]);

    let wrongRegStatus = false;

    let fakeState = '';

    if (status === 'ACTIVE' || status === 'IDLE') {
        fakeState = joined ? 'discoveryActive' : 'discoveryAvailable';
    } else if (status === 'NOT_STARTED') {
        fakeState = 'discoveryComingSoon';
    } else if (status === 'PENDING_REVIEW' || status === 'COMPLETED' ||
        status === 'CANCELLED_DUE_TO_NOT_ENOUGH_PARTICIPANTS'
        || status === 'CANCELLED_BY_ADMIN') {
        fakeState = 'discoveryFinished';
    }

    const { isMobile, isPortrait } = useDeviceSelector(pick(['isMobile', 'isPortrait']));

    const t = useTranslate();

    const tabs = [{ titleKey: t(translations.pageHeaderMysteryParcel), name: 'parcel' },
        { titleKey: t(translations.pageRules), name: 'rules' }];

    const isParcelCompleted = isCompleted(status);

    const lastWinners = useDynamicReducerSelector(state => state.lastWinners);
    const updateWinners = useMemo(
        // eslint-disable-next-line react-hooks/exhaustive-deps
        () => buildWinnersUpdater(winners => dispatch(updateLastWinners(winners)), lastWinners), []);
    const requestedMysteryParcelId = useMemo(extractMysteryParcelId, []);

    if (registrationStatus === isNotStartedRegistrationStatus
        || registrationStatus === isFinishedRegistrationStatus
        || registrationStatus === isInternalRegistrationStatus) {
        wrongRegStatus = true;
    }
    const [isError, setIsError] = useState(false);
    useDetailedView(mysteryParcelId || requestedMysteryParcelId, (parcel) => {
        dispatch(updatePrizes(sortPrizes(getPrizes(parcel))));
        dispatch(updatePrizesWon(sortPrizes(getPrizesWon(parcel))));
        updateWinners(getLastWinners(parcel));
        dispatch(updateJoinState({ joined: getJoined(parcel) }));
        dispatch(updateStatus(getStatus(parcel)));
        dispatch(updateRules(getRules(parcel)));
        dispatch(updateEndTime(getEventEndTime(parcel)));
        dispatch(updateEligible(getEligible(parcel)));
        dispatch(updateStartTime(getEventStartTime(parcel)));
        dispatch(updateMysteryParcelId(getMysteryParcelId(parcel)));
        dispatch(updateParticipatingGamesState(getParticipatingGames(parcel)));
        dispatch(updateDisplayName(getDisplayName(parcel)));
        dispatch(updateRegistrationStatus(getRegistrationStatus(parcel)));
        dispatch(updateTermsAndConditionsUrl(getTermsAndConditionsUrl(parcel)));
        dispatch(updateBannerUrl(getBannerUrl(parcel)));
        dispatch(updateCustomAttributes(getCustomAttributes(parcel)));
        isReceivedDetailsData = true;
    }, () => {
        isReceivedDetailsData = true;
        setIsError(true);
    }, lastWinnersConfig && lastWinnersCount ? lastWinnersCount : lastWinnersDefaultSize);
    usePossessionInfo(mysteryParcelId || requestedMysteryParcelId, (info) => {
        const { currentPossession } = getPossessionInfo(info);
        const { nextPossession } = getPossessionInfo(info);
        if (currentPossession && !isPossessionExist(currentPossession)) {
            possessionsIterations.push(currentPossession);
        } else {
            possessionsIterations = possessionsIterations.map((iteration) => {
                if (iteration.possessionIteration === currentPossession.possessionIteration) {
                    return currentPossession;
                }
                return iteration;
            });
        }
        if (nextPossession && !isPossessionExist(nextPossession)) {
            possessionsIterations.push(nextPossession);
        }
        calculatePossessionInfoTimeToShow();
    });
    const isPossessionExist = (newPossession) => {
        const foundPossession = possessionsIterations
            .find((possession) => possession.possessionIteration === newPossession.possessionIteration);
        return Boolean(foundPossession);
    };

    const calculatePossessionInfoTimeToShow = () => {
        if (possessionsIterations && possessionsIterations.length > 0) {
            const firstItem = possessionsIterations[0];
            if (activePossessionIteration === firstItem.possessionIteration) {
                activePossessionIteration = 0;
                clearTimeout(timer);
            }
            const sumTimeOfAllIterations = possessionsIterations.reduce((sum, current) => {
                if (current.currentPlayerGetsParcel) {
                    return sum + current.possessionTime;
                }
                return sum;
            }, 0);
            const resultTimeToShowCurrentIteration = sumTimeOfAllIterations - (Date.now() - firstItem.possessionStartTime);
            setIsShowPossessionInfo(true);
            activePossessionIteration = firstItem.possessionIteration;
            timer = setTimeout(() => {
                setIsShowPossessionInfo(false);
                activePossessionIteration = 0;
                possessionsIterations = [];
            }, resultTimeToShowCurrentIteration);
        }
    };
    const defineBannerUrl = () => {
        const bannerUrlAttribute = customAttributes.find((attribute) => attribute.name === 'detail_page_image');
        return bannerUrlAttribute ? bannerUrlAttribute.value : bannerUrl;
    };
    const openRules = () => {
        window.scrollTo(0, 0);
        setActivePage(1);
    };

    const trackXButtonClick = () => {
        publicEventsAPIService.trigger('player-clicks-close', { ...commonProperties, ...{
                'overlay.engagementId': mysteryParcelId,
                'game.sidebarButton.group': 'Detailed View Page Top',
                'game.sidebarButton.type': 'Close' } });
    };

    const trackBackButtonClick = () => {
        publicEventsAPIService.trigger('player-clicks-backbutton', { ...commonProperties, ...{
                'overlay.engagementId': mysteryParcelId,
                'game.sidebarButton.group': 'Detailed View Page Top',
                'game.sidebarButton.type': 'Back' } });
    };
    const trackChangeGameClick = (selectedGameCode, positionInLobby) => {
        publicEventsAPIService.trigger('player-changes-game', { ...commonProperties, ...{
                'overlay.engagementId': mysteryParcelId,
                'game.switchMainLaunchAlias': selectedGameCode, positionInLobby } });
    };

    const JoinButton = () => {

        const onJoinButtonClick = useCallback((parcelId) => {
            const str = `{"_type": "com.pt.casino.client.integration.overlay.joinNotification", "id":${parcelId}}`;
            window.parent?.postMessage(str, '*');
        }, []);
        return (<Button
            className={ isParcelCompleted ? style.buttonCompleted : wrongRegStatus && !joined ? style.wrongRegStatus
                : joinError || joined ? style.buttonDone : joining ? style.buttonOkPressed : style.buttonOk}
            onClick={() => {
                if (joining || joined || isParcelCompleted || wrongRegStatus && !joined) {
                    return;
                }
                dispatch(updateJoinState({ joining: true, displaySetNickname: true }));
                dispatch(updateStatusDelay(true));
                setTimeout(() => {
                    dispatch(updateStatusDelay(false));
                }, 1500);
                if (hasNickname || !nicknameNeeded) {
                    onJoinButtonClick(mysteryParcelId);
                    join(mysteryParcelId).then(() => {
                            dispatch(updateJoinState({ joining: false, joined: true }));
                            publicEventsAPIService.trigger('player-clicks-join-mp', { ...commonProperties, ...{
                                    'overlay.engagementId': mysteryParcelId,
                                    'game.sidebarButton.group': 'Detailed View Page',
                                    'game.sidebarButton.type': 'mystery-parcel-opt-in' } });
                            },
                        () => dispatch(updateJoinState({ joining: false, joined: false })));
                }
            }}>
            {joined && !isParcelCompleted && <div className={style.checkMark}/>}
            <span className={style.buttonText}>{t(isParcelCompleted
                ? translations.buttonFinished : joined ? translations.buttonJoined : translations.buttonJoin)}</span>
        </Button>);
    };

    return !isReceivedDetailsData ? <Skeleton/> : isError ? <SkeletonWithError/>
        : <div className={classNames(style.direction, { [style.mainWrapper]: !isMobile,
            [handleMobileView()]: isMobile })}>
        <div className={style.contentWrapper}>
        <div className={classNames(style.detailsTabs, { [style.desktopDetailsTabs] : !isMobile })}>
            <DetailsInnerHeader initialActiveTabIndex={activePage} tabs={tabs}
                                onActiveTabClick={(clickedTabIndex) => {
                                    if (clickedTabIndex !== activePage) {
                                        publicEventsAPIService.trigger('player-changes-tab',
                                            { ...commonProperties, ...{
                                                    'overlay.engagementId': mysteryParcelId,
                                                    currentTab: tabs[clickedTabIndex].name } });
                                    }
                                    setActivePage(clickedTabIndex);
                                }}/>
        </div>

        <div className={style.bannerWrapper}>
            <Media imageUrl={defineBannerUrl()} visible={true} fakeState={fakeState}
                   translatedState={t(fakeState)}/>
            {fakeState !== 'discoveryFinished' && <CountDownBadge className={style.countDownBadge}
                                                                  tKeyPrefix={translationsKeyPrefix}
                                                                  status={status}
                                                                  endTime={new Date(endTime).toISOString()}
                                                                  startTime={new Date(startTime).toISOString()}
                                                                  importedStyle={style}
            />}
            {fakeState === 'discoveryFinished' &&
            <div className={style.resultsConfirmedBadge}>{t(translations.promotionFinished)}</div>}
        </div>

        <div className={classNames({ [style.hideTab]: activePage !== 0 })}>
            {status !== '' && <div className={registrationStatus === 'INTERNAL' && !joined ? style.buttonBlockCollapsed : style.buttonBlock}>
                {registrationStatus !== 'INTERNAL' && <JoinButton />}
                {registrationStatus === 'INTERNAL' && joined && <JoinButton />}
            </div>}
            <div className={style.titleBlock}>
                <span className={style.titleText}>{t(translations.titleYourStatus)}</span>
            </div>

            <Status isShowPossessionInfo={joined && isShowPossessionInfo} onCLickNotEligible={openRules}
                    customStyle={classNames(style.statusCustomStyle, { [style.statusCustomStyleMobile] : isMobile })} />

            {isParcelCompleted
                ? <>
                    <div className={style.titleBlock}>
                        <span className={style.titleText}>{t(translations.titleMyPrizes)}</span>
                    </div>

                    <Panel><PrizesWonTable /></Panel>
                </>
                : <>
                    <div className={style.titleBlock}>
                        <span className={style.titleText}>{t(translations.titlePrizes)}</span>
                    </div>

                    <Panel><AwardsTable /></Panel>
                </>}

            <div className={style.titleBlock}>
                <span className={style.titleText}>{t(translations.titleLatestWinners)}</span>
            </div>

            <Panel><WinnersTable /></Panel>
        </div>
        <div className={classNames({ [style.hideTab]: activePage !== 1 })}>
            {status !== '' && <div className={registrationStatus === 'INTERNAL' && !joined ? style.buttonBlockCollapsed : style.buttonBlock}>
                {registrationStatus !== 'INTERNAL' && <JoinButton />}
                {registrationStatus === 'INTERNAL' && joined && <JoinButton />}
            </div>}
            <div className={style.titleBlock}>
                <span className={classNames(style.titleText)}>{t(translations.pageRules)}</span>
            </div>
            <div className={style.block}>
                <Rules classname={style.customRules} textRules={rules} />
            </div>
        </div>

            {!hideGamePanel && <div className={style.block}>
                <div className={style.titleBlock}>
                    <span className={classNames(style.titleText)}>{t(translations.titleIncludedGames)}</span>
                </div>
                <GamesView
                    gameCodes={participatingGames}
                    isShowGameName={false}
                    isShowPlayButtonOnHover={true}
                    trackChangeGameClick={trackChangeGameClick}
                />
            </div>}

            <div className={classNames(style.block, style.center)}>
                <TermsAndConditions className={style.termsAndConditions} termsAndConditionsUrl={termsAndConditionsUrl}
                                    text={t(translations.termsAndConditions)}/>
            </div>

        <div className={classNames(style.buttonBlock, style.bottomButtonBlock,
            { [style.buttonsInColumn]: isMobile && isPortrait })}>
            { activePage !== 1 && <Button className={classNames(style.buttonHowToPlayOk, style.buttonHowToPlay)}
                    pressedClass={style.buttonHowToPlayPressed}
                    onClick={() => {
                        openRules();
                    }}>
                <span className={style.buttonText}>{t(translations.buttonHowToPlay)}</span>
            </Button> }
            {joined && !isParcelCompleted && <>
                <Button className={style.buttonLeave} pressedClass={style.buttonLeavePressed} onClick={() => {
                    leave(mysteryParcelId).then(() => {
                        dispatch(updateJoinState({ joined: false }));
                    }, () => {
                    });
                    publicEventsAPIService.trigger('player-clicks-leave-mp', {
                        ...commonProperties, ...{
                            'overlay.engagementId': mysteryParcelId,
                            'game.sidebarButton.group': 'Detailed View Page',
                            'game.sidebarButton.type': 'mystery-parcel-opt-out'
                        }
                    });
                }}>
                    <span className={style.buttonText}>{t(translations.buttonLeaveParcel)}</span>
                </Button>
            </>
            }
        </div>
        </div>
        {displayName && <HeaderBanner className={style.mainHeaderStyle} text={displayName} useBackButton={true} widgetType={'mp'}
                                      backButtonUrl={'/leaderboard-list'} trackXButtonClick={trackXButtonClick}
                                      trackBackButtonClick={trackBackButtonClick}/>}
    </div>;
};
